import * as THREE from "three";

/**
 * The hub's triangle budget (ui.md F7, art wow pass Phase 2). The hub is an idle menu, yet it measured 1.9 M
 * triangles / 184 draws — the level's micro-detail scatter (≈1,000-triangle tufts over every apron cell) invisible at
 * hub zoom, plus a camera spinning forever. A phone heated up sitting on the menu. The hub now fits in `Budget`.
 *
 * The camera is fixed now (the lobby redesign: a still shot, no sway), so the hub also spends less on what it draws:
 * the fortress and towers stand at reduced detail (`ModelLod`), the scenery is a sparse ring at the haze instead of
 * the level's per-cell dressing, and frames are rendered on demand (hubRender.ts).
 */
export const HUB_BUDGET = {
  /** Total triangles the hub may draw (ui.md F7 target). */
  Budget: 300_000,
  /**
   * Share of the budget the base + tower stands may take before stands are left out (newest first). 0.8 since
   * TOWERS_V2: twelve towers at 0.7 of the budget and 0.4 detail left the last three off the hub.
   */
  ModelShare: 0.8,
  /**
   * Detail the fortress and towers keep at the hub (lod.ts): the art is budgeted for a tower seen up close in a
   * battle (~30 k triangles, the fortress ~100 k), and the hub shows them 60–150 px tall. `ratio` is the share of
   * triangles kept, `error` the deviation allowed relative to the model's size.
   */
  ModelLod: { ratio: 0.3, error: 0.004 },
  /** Meshes smaller than this keep their full detail (glow cards, pennants): too few triangles to be worth a copy. */
  LodMinTriangles: 4_000,
} as const;

/** Triangles drawn by `root` and everything under it (instanced meshes count every instance). */
export function countTriangles(root: THREE.Object3D): number {
  let tris = 0;
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !m.geometry) return;
    const g = m.geometry;
    const perDraw = (g.index ? g.index.count : (g.attributes.position?.count ?? 0)) / 3;
    const inst = (m as THREE.InstancedMesh).isInstancedMesh ? (m as THREE.InstancedMesh).count : 1;
    tris += perDraw * inst;
  });
  return Math.round(tris);
}

/**
 * How many of the models (base first, then stands in unlock order) fit in `cap` triangles: the base always stays, then
 * stands are kept while they fit.
 */
export function modelsThatFit(trisPerModel: readonly number[], cap: number): number {
  let used = 0, n = 0;
  for (const t of trisPerModel) {
    if (n > 0 && used + t > cap) break;
    used += t; n++;
  }
  return n;
}
